import React, { useState, useEffect, useMemo } from "react";
import { format, parseISO, eachDayOfInterval, addDays, subDays, } from "date-fns";
import { Table, InputNumber, Input, Button, message, Card, Modal, Descriptions, Space, Tag, Divider, Alert } from "antd";
import { LeftOutlined, RightOutlined, CheckCircleOutlined, MedicineBoxOutlined, EditOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import '../../../styles/timesheet.css';
import { USERURL, ACCOUNTURL } from "../config/config";
import { toast } from "react-toastify";
import { Fetchdetails } from "../../fetchdetails/fetchdetails";
import EmailModal from "../modal/dynamicModal";
import ConfirmationModal from "../modal/ConfirmationModal";

function Timesheet() {
    const [periods, setPeriods] = useState([]);
    const [sickLeaveCount, setSickLeaveCount] = useState("")
    const [declineCount, setDeclineCount] = useState("")
    const [currentIndex, setCurrentIndex] = useState(null);
    const [startIndex, setStartIndex] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [timesheetData, setTimesheetData] = useState([]);
    const [activeTab, setActiveTab] = useState("timesheet");
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [isDeclineModalVisible, setIsDeclineModalVisible] = useState(false);
    const [selectedDeclinedTimesheet, setSelectedDeclinedTimesheet] = useState(null);
    const candidateId = localStorage.getItem('candidate_id')
    const [editedList, setEditedList] = useState([])
    const [modalTimesheetData, setModalTimesheetData] = useState([]);
    const [mstatus, setMstatus] = useState('');
    const [comments, setComments] = useState('');
    const [weeksnew, setWeeksnew] = useState('')
    const [dweeknew, setDweeknew] = useState('');
    const [editid, setEditid] = useState('');
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [selectedEmails, setSelectedEmails] = useState([]);

    const visibleCount = 4;
    const currentPeriod = useMemo(() => periods[currentIndex], [periods, currentIndex]);
    const periodRange = useMemo(() =>
        currentPeriod?.status !== "future"
            ? `${currentPeriod?.weekstart}/${currentPeriod?.weekend}`
            : "",
        [currentPeriod]
    );

    const [errors, setErrors] = useState({
        'finalmessage': ''
    });

    const [fieldErrors, setFieldErrors] = useState({});
    const [validationMessage, setValidationMessage] = useState('');

    const formatPeriodDisplay = (start, end) =>
        `${format(new Date(start), "MMM d")} - ${format(new Date(end), "MMM d,yyyy")}`;

    const isDateInPeriod = (date, start, end) => {
        const checkDate = new Date(date);
        const startDate = new Date(start);
        const endDate = new Date(end);
        return checkDate >= startDate && checkDate <= endDate;
    };

    // Add function to determine period status
    const getPeriodStatus = (period) => {
        const today = new Date();
        const startDate = new Date(period.weekstart);
        const endDate = new Date(period.weekend);

        if (isDateInPeriod(today, period.weekstart, period.weekend)) {
            return 'current';
        } else if (today < startDate) {
            return 'future';
        }
        return 'past';
    };

    useEffect(() => {
        const fetchWeekList = async (id) => {
            if (!id) {
                console.warn("candidate_id not found in local storage.");
                toast.warn("Candidate ID not found.", { duration: 2000 });
                return;
            }

            const token = localStorage.getItem('access_token');
            const api = `${ACCOUNTURL}getcandidateweeklist/${id}`;

            const postData = {
                method: 'get',
                headers: {
                    'content-type': 'application/json',
                    'authorization': token
                },
            };

            const response = await Fetchdetails(api, postData);
            setSickLeaveCount(response?.res?.sickleave)
            setDeclineCount(response?.res?.editcount)
            setPeriods(response?.res.data);


        };

        const getEditedList = async (id) => {
            if (!id) {
                console.warn("candidate_id not found in local storage.");
                toast.warn("Candidate ID not found.", { duration: 2000 });
                return;
            }

            const token = localStorage.getItem('access_token');
            const api = `${ACCOUNTURL}gettseditlist/${id}`;

            const postData = {
                method: 'get',
                headers: {
                    'content-type': 'application/json',
                    'authorization': token
                },
            };

            const response = await Fetchdetails(api, postData);
            setEditedList(response?.res?.datalist)
            if (response?.actionMsg === 1 && response?.res?.actionMsg === 1) {
                toast.success(response.res.message)

            } else {
                console.error("Failed to fetch week list:", response?.res?.Message || "Contact admin");
                toast.error(response?.res?.Message || 'Failed to fetch week list', { duration: 2000 });
                setPeriods([]);
            }
        };

        if (candidateId) {
            fetchWeekList(candidateId);
            getEditedList(candidateId)
        }

    }, [candidateId]);

    useEffect(() => {
        if (!periodRange) return;
        const [start, end] = periodRange.split("/");
        const days = eachDayOfInterval({ start: parseISO(start), end: parseISO(end) });
        const localData = localStorage.getItem(`timesheet_${periodRange}`);
        const parsed = localData ? JSON.parse(localData) : null;

        const filled = days.map((date, i) => {
            const dateStr = format(date, "yyyy-MM-dd");
            const saved = parsed?.timesheetData?.find(d => d.date === dateStr);
            return {
                key: i,
                date: dateStr,
                day: format(date, "EEEE"),
                hours: saved?.hours || null,
                reason: saved?.reason || "",
                isSickLeave: saved?.isSickLeave || false
            };
        });
        setTimesheetData(filled);
    }, [periodRange]);

    const handleChange = (val, index, field) => {
        setTimesheetData(prev => {
            const updated = [...prev];
            updated[index][field] = val;
            return updated;
        });
    };

    const handleModalChange = (val, index, field) => {
        setModalTimesheetData(prev => {
            const updated = [...prev];
            updated[index][field] = val;
            return updated;
        });
    };

    const handleSave = async () => {
        try {
            let token = localStorage.getItem('access_token');
            let datastring = {
                'candidate_id': candidateId,
                'timesheet': timesheetData.map(item => ({
                    date: item.date,
                    day: item.day,
                    hours: item.hours || 0,
                    reason: item.reason || '',
                    status: item.status
                })),
                'weeks': selectedPeriod
            };

            let api = `${ACCOUNTURL}savetimeseheet`
            let postData = {
                'method': 'post',
                'headers': { 'content-type': 'application/json', 'authorization': token },
                'body': JSON.stringify(datastring)
            }
            let response = await Fetchdetails(api, postData);
            if (response.actionMsg === 1) {
                let res = response.res;
                if (res.actionMsg === 0) {
                    setErrors((prevErrors) => ({ ...prevErrors, finalmessage: res.Message }))
                }
                else {
                    toast.success('' + res.Message + '', { duration: 2000 });
                }
            }
            else {
                toast.error('' + response.Message + '', { duration: 2000 });
            }
        }
        catch (error) {
            let er = error.message || 'contact admin'
            toast.error(er, { duration: 2000 })
        }
    }

    const handleSubmitTimesheet = async (email) => {
        setIsSubmitting(true);
        try {
            let token = localStorage.getItem('access_token');
            let datastring = {
                'candidate_id': candidateId,
                'weeks': selectedPeriod,
                'memail': email,
                'timesheet': timesheetData
            };

            let api = `${ACCOUNTURL}sendctsapproval`
            let postData = {
                'method': 'post',
                'headers': { 'content-type': 'application/json', 'authorization': token },
                'body': JSON.stringify(datastring)
            }
            let response = await Fetchdetails(api, postData);
            if (response.actionMsg === 1) {
                let res = response.res;
                toast.success('' + res.Message + '', { duration: 2000 });
                if (res.actionMsg === 0) {
                    setErrors((prevErrors) => ({ ...prevErrors, finalmessage: res.Message }))
                } else {
                    setIsConfirmModalVisible(false);
                    window.location.reload()
                }
            }
            else {
                toast.error('' + response.Message + '', { duration: 2000 });
            }
        }
        catch (error) {
            let er = error.message || 'contact admin'
            toast.error(er, { duration: 2000 })
        }
        finally {
            setIsSubmitting(false);
        }
    }



    const handleResubmitTimesheet = async () => {
        setIsSubmitting(true);
        try {
            const token = localStorage.getItem("access_token");
            const payload = {
                candidate_id: candidateId,
                timesheet: modalTimesheetData.map(day => ({
                    date: day.date,
                    hours: day.hours.toString(),
                    reason: day.reason
                })),
                weeks: weeksnew,
                dweeks: dweeknew,
                id: editid
            };


            const api = `${ACCOUNTURL}saveupdatetimeseheet`;
            const postData = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                body: JSON.stringify(payload)
            };

            const response = await Fetchdetails(api, postData);
            console.log("responseeee??", response)

            if (response.actionMsg === 1) {
                toast.success(response.res.Message || "Timesheet resubmitted successfully!");
                window.location.reload()
                // setIsDeclineModalVisible(false);
                // setEditedList(candidateId);
            } else {
                throw new Error(response.res.Message || "Failed to resubmit timesheet");
            }
        } catch (err) {
            toast.error(err.message || "Error resubmitting timesheet");
        } finally {
            setIsSubmitting(false);
        }
    };



    const getEditedByList = async (id) => {

        try {
            let token = localStorage.getItem('access_token');
            let datastring = {
                'candidate_id': candidateId,
                'id': id
            };

            let api = `${ACCOUNTURL}gettsinvedit`
            let postData = {
                'method': 'post',
                'headers': { 'content-type': 'application/json', 'authorization': token },
                'body': JSON.stringify(datastring)
            }
            let response = await Fetchdetails(api, postData);

            if (response.actionMsg === 1) {
                let res = response?.res;
                const modalData = res?.data?.map(day => ({
                    date: day.date,
                    hours: parseInt(day.hours) || 0,
                    reason: day.reason || '',
                    isSickLeave: false
                }));

                setModalTimesheetData(modalData);
                setIsDeclineModalVisible(true);
                setComments(res.comments)
                setWeeksnew(res.weeks)
                setDweeknew(res.dweek)
                setEditid(id);

                toast.success('' + res.Message + '', { duration: 2000 });
            }
            else {
                toast.error('' + response.Message + '', { duration: 2000 });
            }
        }
        catch (error) {
            let er = error.message || 'contact admin'
            toast.error(er, { duration: 2000 })
        }
    }


    const handleweek = async (weeks) => {
        setSelectedPeriod(weeks)
        try {
            console.log(selectedPeriod)
            let token = localStorage.getItem('access_token');
            let datastring = {
                'candidate_id': candidateId,
                'weeks': weeks
            };

            let api = `${ACCOUNTURL}getweekdays`;
            let postData = {
                'method': 'post',
                'headers': { 'content-type': 'application/json', 'authorization': token },
                'body': JSON.stringify(datastring)
            };

            let response = await Fetchdetails(api, postData);
            if (response.actionMsg === 1) {
                let res = response.res;
                if (res.actionMsg === 0) {
                    setErrors((prevErrors) => ({ ...prevErrors, 'finalmessage': res.Message }));
                } else {
                    setTimesheetData(res.datalist);
                    setMstatus(res.mstatus)
                    // toast.success(res.Message || 'Timesheet data loaded successfully', { duration: 2000 });
                }
            } else {
                toast.error(response.Message || 'Failed to fetch timesheet data', { duration: 2000 });
            }
        } catch (error) {
            let er = error.message || 'contact admin';
            toast.error(er, { duration: 2000 });
        }
    };

    const handleEditDeclinedTimesheet = (timesheet) => {
        setSelectedDeclinedTimesheet(timesheet);
        getEditedByList(timesheet?.id);
        const [start, end] = timesheet.weeks.split(" / ");
        const days = eachDayOfInterval({
            start: parseISO(start),
            end: parseISO(end)
        });

        const newTimesheetData = days.map((date, i) => ({
            key: i,
            date: format(date, "yyyy-MM-dd"),
            day: format(date, "EEEE"),
            hours: 0,
            reason: "",
            isSickLeave: false
        }));

        setModalTimesheetData(newTimesheetData);
        setIsDeclineModalVisible(true);
    };


    const validateTimesheetFields = () => {
        let hasError = false;
        let totalHours = 0;
    
        const days = timesheetData?.slice(0, 7) || [];
    
        for (let i = 0; i < days.length; i++) {
            const day = days[i];
    
            if (day.status === "enable") {
                if (day.hours === '' || day.hours === null || Number(day.hours) < 0) {                    
                    hasError = true;
                } else {
                    totalHours += Number(day.hours);
                }

            }
        }
    
        if (!hasError && (totalHours === 0 )) {
            hasError = true;
        }
        
        
        if (hasError) {
            toast.error("Please fill all fields in the timesheet", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });

        return false
        }
    
        return true;
    };
    
    const handleSubmitClick = () => {
        const isValid = validateTimesheetFields();
        console.log(isValid,"oooooooooooooooooooooo");
                
        if (isValid) {
            setModalVisible(true);
        }
    };

    const handleConfirmSubmit = (emails) => {
        setModalVisible(false);
        setIsConfirmModalVisible(true);
        setSelectedEmails(emails);
    };

    const handleFinalSubmit = async () => {
        if (selectedEmails && selectedEmails.length > 0) {
            await handleSubmitTimesheet(selectedEmails);
        }
    };

    return (
        <>
            <title>Timesheet</title>
            <div className="timesheet-container" style={{ marginTop: '30px' }}>
                <Card className="header-card">
                    <h1>Timesheet</h1>
                    <p className="subtitle">Record your daily hours and leaves</p>
                </Card>

                <div className="period-and-stats-container">
                    <Card className="period-card-container">
                        <h3>Select Period</h3>
                        <div className="period-slider-wrapper">

                            <div className="period-cards-container">
                                <div style={{
                                    display: 'flex',
                                    gap: '16px',
                                    padding: '8px 0',
                                    overflowY: 'auto'
                                }}>

                                    {periods?.map((item, index) => {

                                        let cardStyle = {
                                            minWidth: '200px',
                                            color: '#fff',
                                            padding: '0px',
                                            cursor: 'pointer'
                                        };

                                        let isClickable = true;

                                        switch (item.fstatus) {
                                            case 'Approved':
                                                cardStyle.backgroundColor = 'green';
                                                break;
                                            case 'Submited':
                                                cardStyle.backgroundColor = 'grey';
                                                cardStyle.color = '#000';
                                                cardStyle.cursor = 'not-allowed';
                                                isClickable = false;
                                                break;
                                            case 'Pending':
                                                cardStyle.backgroundColor = '#f30';
                                                break;
                                            case 'Current':
                                                cardStyle.backgroundColor = 'blue';
                                                break;
                                            case 'Future':
                                                cardStyle.backgroundColor = 'grey';
                                                cardStyle.color = '#000';
                                                cardStyle.cursor = 'not-allowed';
                                                isClickable = false;
                                                break;
                                            case 'Disabled':
                                                cardStyle.backgroundColor = 'grey';
                                                cardStyle.color = '#000';
                                                cardStyle.cursor = 'not-allowed';
                                                isClickable = false;
                                                break;
                                            default:
                                                return null;
                                        }

                                        return (
                                            <Card
                                                key={index}
                                                onClick={isClickable ? () => handleweek(item.value) : undefined}
                                                style={cardStyle}
                                            >
                                                <h5 style={{ margin: 0, padding: '5px' }}>{item.label}</h5>
                                                <h5 style={{ margin: 0, padding: '5px' }}>{item.fstatus}</h5>
                                            </Card>
                                        );
                                    })}


                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card className="quick-stats-card" bordered>
                        <h3 style={{ textAlign: 'center', marginBottom: '16px' }}>Quick Status</h3>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                maxWidth: '250px',
                                padding: '8px 16px',
                                border: '1px solid #f0f0f0',
                                borderRadius: '8px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <MedicineBoxOutlined style={{ color: '#cf1322', marginRight: 8 }} />
                                    <span>Sick Leave Days</span>
                                </div>
                                <span style={{
                                    color: '#cf1322',
                                    fontWeight: 'bold',
                                    fontSize: '16px'
                                }}>
                                    {sickLeaveCount ? sickLeaveCount : "0"}
                                </span>
                            </div>
                        </div>
                    </Card>
                </div>
                <div className="timesheet-table-container">


                    <Card
                        title="Daily Entries"
                        bordered
                        className="timesheet-entry-table"
                    >
                        {timesheetData.length > 0 ? (
                            <>
                                <div className="timesheet-grid">
                                    <div className="timesheet-header">
                                        <div className="timesheet-cell">Date</div>
                                        {Array.isArray(timesheetData) &&
                                            timesheetData.slice(0, 7).map((day) => (
                                                <div key={day.date} className="timesheet-cell">
                                                    {format(parseISO(day.date), "EEE MM/dd")}
                                                </div>
                                            ))}
                                        <div className="timesheet-cell">Total</div>
                                    </div>

                                    <div className="timesheet-row">
                                        <div className="timesheet-cell">Hours</div>
                                        {timesheetData.slice(0, 7).map((day, index) => (
                                            <div key={day.date} className={`timesheet-cell ${fieldErrors[index]?.hours || fieldErrors[index]?.reason ? 'has-error' : ''}`}>
                                                {day.status === "enable" ? (
                                                    <div className="input-container">

                                                        <InputNumber
                                                            min={0}
                                                            max={24}
                                                            value={day.hours === 0 ? 0 : day.hours || undefined}
                                                            onChange={(v) => handleChange(v, index, "hours")}
                                                            status={fieldErrors[index]?.hours ? "error" : undefined}
                                                        />
                                                        {/* {fieldErrors[index]?.hours && (
                                                            <div className="error-message">
                                                                {fieldErrors[index].hours}
                                                            </div>
                                                        )} */}
                                                    </div>
                                                ) : (
                                                    <span>{day.hours}</span>
                                                )}
                                            </div>
                                        ))}
                                        <div className="timesheet-cell timesheet-total">
                                            {timesheetData.reduce(
                                                (sum, day) => sum + (Number(day.hours) || 0),
                                                0
                                            )}
                                        </div>
                                    </div>

                                    <div className="timesheet-row">
                                        <div className="timesheet-cell">Reason</div>
                                        {timesheetData.slice(0, 7).map((day, index) => (
                                            <div key={day.date} className={`timesheet-cell ${fieldErrors[index]?.reason ? 'has-error' : ''}`}>
                                                {day.status === "enable" ? (
                                                    <div className="input-container">
                                                        <Input
                                                            // placeholder="Enter reason"
                                                            value={day.reason}
                                                            onChange={(e) =>
                                                                handleChange(e.target.value, index, "reason")
                                                            }
                                                            status={fieldErrors[index]?.reason ? "error" : undefined}
                                                        />
                                                        {/* {fieldErrors[index]?.reason && (
                                                            <div className="error-message">
                                                                {fieldErrors[index].reason}
                                                            </div>
                                                        )} */}
                                                    </div>
                                                ) : (
                                                    <span>{day.reason || "-"}</span>
                                                )}
                                            </div>
                                        ))}
                                        <div className="timesheet-cell"></div>
                                    </div>

                                    <div className="timesheet-row timesheet-total-row">
                                        <div className="timesheet-cell">Total</div>
                                        {timesheetData.slice(0, 7).map((day) => (
                                            <div
                                                key={`total-${day.date}`}
                                                className="timesheet-cell timesheet-total"
                                            >
                                                {day.hours || 0}
                                            </div>
                                        ))}
                                        <div className="timesheet-cell timesheet-total">
                                            {timesheetData.reduce(
                                                (sum, day) => sum + (Number(day.hours) || 0),
                                                0
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {
                                    (mstatus === 0)
                                        ?
                                        <div className="submit-container">
                                            <Button
                                                onClick={handleSave}
                                                disabled={
                                                    isSubmitting ||
                                                    !Array.isArray(timesheetData) ||
                                                    timesheetData.every((day) => day.status === "Approved")
                                                }
                                            >
                                                Save Changes
                                            </Button>
                                            <Button
                                                type="primary"
                                                onClick={handleSubmitClick}
                                                loading={isSubmitting}
                                                disabled={
                                                    isSubmitting ||
                                                    !Array.isArray(timesheetData) ||
                                                    timesheetData.every((day) => day.status === "Approved")
                                                }
                                            >
                                                Submit for Approval
                                            </Button>
                                        </div>
                                        :
                                        ''
                                }

                            </>
                        ) : (
                            <Card className="disabled-message" bordered={false}>
                                <p>Select a period to view timesheet.</p>
                            </Card>
                        )}
                    </Card>

                    <Card className="decline-container" style={{ marginTop: 24 }}>
                        <h3>Declined Timesheets</h3>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            maxWidth: '250px',
                            padding: '8px 16px',
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <MedicineBoxOutlined style={{ color: '#cf1322', marginRight: 8 }} />
                                <span>Declined Timesheets</span>
                            </div>
                            <span style={{
                                color: '#cf1322',
                                fontWeight: 'bold',
                                fontSize: '16px'
                            }}>
                                {declineCount ? declineCount : "0"}
                            </span>
                        </div>
                        {editedList?.length > 0 ? (
                            <Table
                                dataSource={editedList}
                                columns={[
                                    {
                                        title: 'ID',
                                        dataIndex: 'autoId',
                                        key: 'autoId',
                                        align: 'center'
                                    },
                                    {
                                        title: 'Weeks',
                                        dataIndex: 'label',
                                        key: 'weeks',
                                        align: 'center',
                                        render: (text, record) => (
                                            <Button
                                                type="link"
                                                onClick={() => handleEditDeclinedTimesheet(record)}
                                            >
                                                {text}
                                            </Button>
                                        )
                                    },
                                    {
                                        title: 'Hours',
                                        dataIndex: 'hours',
                                        key: 'hours',
                                        align: 'center'
                                    }
                                ]}
                                pagination={false}
                                size="small"
                            />
                        ) : (
                            <p>No declined timesheets found</p>
                        )}

                        <Modal
                            title="Edit Declined Timesheet"
                            open={isDeclineModalVisible}
                            onCancel={() => setIsDeclineModalVisible(false)}
                            width={"80%"}
                            footer={[
                                <Button key="cancel" onClick={() => setIsDeclineModalVisible(false)}>
                                    Cancel
                                </Button>,
                                <Button
                                    key="submit"
                                    type="primary"
                                    onClick={handleResubmitTimesheet}
                                    loading={isSubmitting}
                                >
                                    Resubmit
                                </Button>
                            ]}
                        >
                            {modalTimesheetData.length > 0 && (
                                <div>
                                    <div className="timesheet-grid">
                                        <div className="timesheet-header">
                                            <div className="timesheet-cell">Date</div>
                                            {modalTimesheetData.slice(0, 7).map((day) => (
                                                <div key={day.date} className="timesheet-cell">
                                                    {format(parseISO(day.date), "EEE MM/dd")}
                                                </div>
                                            ))}
                                            <div className="timesheet-cell">Total</div>
                                        </div>

                                        <div className="timesheet-row">
                                            <div className="timesheet-cell">Hours</div>
                                            {modalTimesheetData.slice(0, 7).map((day, index) => (
                                                <div key={day.date} className="timesheet-cell">
                                                    <InputNumber
                                                        min={0}
                                                        max={24}
                                                        value={day.hours}
                                                        onChange={(v) => handleModalChange(v, index, "hours")}
                                                        disabled={day.isSickLeave}
                                                        style={{ width: "100%" }}
                                                    />
                                                </div>
                                            ))}
                                            <div className="timesheet-cell timesheet-total">
                                                {modalTimesheetData.reduce((sum, day) => sum + (day.hours || 0), 0)}
                                            </div>
                                        </div>

                                        <div className="timesheet-row">
                                            <div className="timesheet-cell">Reason</div>
                                            {modalTimesheetData.slice(0, 7).map((day, index) => (
                                                <div key={day.date} className="timesheet-cell">
                                                    <Input
                                                        placeholder="Enter reason"
                                                        value={day.reason}
                                                        onChange={(e) => handleModalChange(e.target.value, index, "reason")}
                                                        disabled={day.isSickLeave}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="timesheet-row timesheet-total-row">
                                            <div className="timesheet-cell">Total</div>
                                            {modalTimesheetData?.slice(0, 7).map((day) => (
                                                <div key={`total-${day.date}`} className="timesheet-cell timesheet-total">
                                                    {day.hours || 0}
                                                </div>
                                            ))}
                                            <div className="timesheet-cell timesheet-total">
                                                {modalTimesheetData?.reduce((sum, day) => sum + (day.hours || 0), 0)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div>Comments</div>
                            <div>{comments}</div>
                        </Modal>
                    </Card>

                </div>

            </div>
            <ConfirmationModal
                visible={isConfirmModalVisible}
                onConfirm={handleFinalSubmit}
                onCancel={() => setIsConfirmModalVisible(false)}
                loading={isSubmitting}
            />
            <EmailModal
                visible={modalVisible}
                onSubmit={handleConfirmSubmit}
                onCancel={() => setModalVisible(false)}
            />
        </>

    );
}

export default Timesheet;